// Components
import { Box, Button, Dialog, DialogActions, FormHelperText, Grid, Tooltip } from "@mui/material";
import { MultipleAutocompleteComponent } from "src/views/marketing-actions/components/form/MultipleAutocomplete";
import { OfferCharacteristicSharedInput } from "src/views/marketing-actions/components/form/OfferCharacteristicCard/OfferCharacteristicSharedInput";
import {
    type OptionType,
    RadioGroupComponent,
} from "src/views/marketing-actions/components/form/RadioGroup";
import type { CustomScenario } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard";
import { useScenarioCardStyles } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard.styles";

// Types
import type { IdName } from "src/types/IdName";
import { MarketingActionVariantEnum } from "src/utils/variants";
import type { MultipleSelectFieldType } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.utils";

// Libs
import { type FieldInputProps, useField } from "formik";
import { useMemo } from "react";
import { type TFunction, useTranslation } from "src/design-system/core/TranslationContext";

// Utils
import { createFilterOptions } from "@mui/material";
import {
    buildTriggerPercentScenarioOptions,
    optionBuilderForTriggerScenarioValues,
} from "src/utils/trigger";

import type { Moment } from "moment";
import {
    formatPercentValueForScenarios,
    generateEdges,
} from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";

export const MIN_EDGE_VALUE = 0;
export const MAX_EDGE_VALUE = 999;

export interface OptionTypeEdges {
    inputValue?: string;
    name?: string;
    id?: number | string;
}

const filter = createFilterOptions<OptionTypeEdges>();

interface AddScenarioDialogProps {
    t: TFunction;
    recurrenceValue: {
        startDate: Moment;
        endDate: Moment;
        startTime: number;
        endTime: number;
        days: number[];
    };
    onCancel: () => void;
    onSave: () => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    setFieldValueScenario: (field: string, value: any, shouldValidate?: boolean) => void;
    isSpecialOperation: boolean;
    audienceField: { name: string; option: OptionType[]; handleChange: (v) => void };
    serviceAndChannelField: FieldInputProps<any>;
    offerCharacteristicField: MultipleSelectFieldType;
    productField: MultipleSelectFieldType;
    categoryItemListField: MultipleSelectFieldType;
    cartField: MultipleSelectFieldType;
    discountField: MultipleSelectFieldType;
    validatedScenarios: CustomScenario[];
    isOpen: boolean;
    selectedSource: string;
    maxItems: number;
    selectedChannel: string;
    selectedPosList: IdName[];
    isUpdate: boolean;
    idScenario: number;
}

export const AddScenarioDialog = ({
    t,
    recurrenceValue,
    isOpen,
    onCancel,
    onSave,
    audienceField,
    isSpecialOperation,
    offerCharacteristicField,
    maxItems,
    productField,
    categoryItemListField,
    selectedChannel,
    selectedSource,
    selectedPosList,
    setFieldValue,
    setFieldValueScenario,
    discountField,
    cartField,
    serviceAndChannelField,
    validatedScenarios,
    isUpdate,
    idScenario,
}: AddScenarioDialogProps): JSX.Element => {
    const classes = useScenarioCardStyles();
    const { translateCommon } = useTranslation();
    const [field] = useField("scenarioTemp");
    const [fieldTriggerMin, metaTriggerMin, helpersTriggerMin] =
        useField("scenarioTemp.triggerMin");
    const [fieldTriggerMax, metaTriggerMax, helpersTriggerMax] =
        useField("scenarioTemp.triggerMax");

    const percentOptions = useMemo(() => {
        return buildTriggerPercentScenarioOptions();
    }, []);

    const optionTriggerMin = useMemo(() => {
        if (isOpen === false) {
            return [];
        }
        const edges = generateEdges();
        const filteredOption = percentOptions.filter((e) => {
            return e.id >= edges.min && e.id <= edges.max;
        });

        return optionBuilderForTriggerScenarioValues(fieldTriggerMin, filteredOption);
    }, [isOpen, fieldTriggerMin.value, idScenario, validatedScenarios, isUpdate]);

    const optionTriggerMax = useMemo(() => {
        if (isOpen === false) {
            return [];
        }
        const edges = generateEdges();
        const filteredOption = percentOptions.filter((e) => {
            return e.id >= edges.min && e.id <= edges.max;
        });
        return optionBuilderForTriggerScenarioValues(fieldTriggerMax, filteredOption);
    }, [isOpen, fieldTriggerMax.value, idScenario, validatedScenarios, isUpdate]);

    const filterOptionsFn = (options: any, params: any): OptionType[] => {
        const filtered = filter(options, params);

        const numberRegex = /^[0-9]{1,3}$/i;
        const percentRegex = /^[0-9]{1,3}%$/i;

        if (params.inputValue !== "") {
            if (numberRegex.test(params.inputValue) || percentRegex.test(params.inputValue)) {
                const index = params.inputValue.includes("%")
                    ? filtered.findIndex((e) => e.name === params.inputValue)
                    : filtered.findIndex((e) => e.id?.toString() === params.inputValue);
                if (index === -1) {
                    const newPercentValue = params.inputValue.includes("%")
                        ? params.inputValue.split("%")[0]
                        : params.inputValue;
                    const percentValue = Number.parseInt(newPercentValue, 10);
                    const positivePercentValue = formatPercentValueForScenarios(percentValue);
                    filtered.push({
                        inputValue: `+${percentValue}`,
                        name: `${translateCommon("actions.add")} ${positivePercentValue}`,
                    });
                }
            }
        }

        return filtered;
    };

    const generateToolTipSubmitButton = () => {
        if (field?.value?.isCheeckyTuesday) {
            return t("cheecky-submit-tooltip");
        }
        return "";
    };

    const disableSubmitButtonTooltipText = generateToolTipSubmitButton();

    return (
        <Dialog
            open={isOpen}
            maxWidth="lg"
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <Box className={classes.scenarioDialogContainer}>
                <span className={classes.titleScenarioDialog}>
                    {!isUpdate &&
                        t(
                            `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.title-create`
                        )}
                    {isUpdate &&
                        t(
                            `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.title-update`
                        )}
                </span>
                <Box className={classes.subTitleScenarioDialogContainer}>
                    <span className={classes.subTitleScenarioDialog}>
                        {t(
                            `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.trigger-title`
                        )}
                    </span>
                </Box>

                <Box className={classes.edgesScenarioContainer}>
                    {t(
                        `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.trigger-part-one`
                    )}
                    <Box className={classes.edgesScenarioLeft}>
                        <MultipleAutocompleteComponent
                            inputProps={{}}
                            variant="standard"
                            freeSolo={false}
                            classes={{ popper: classes.popper }}
                            name={"scenarioTemp.triggerMin"}
                            multiple={false}
                            options={optionTriggerMin}
                            filterOptions={filterOptionsFn}
                            onOpen={() => {
                                if (!fieldTriggerMin) {
                                    helpersTriggerMin.setValue(fieldTriggerMin.value, false);
                                }
                            }}
                            isDisplayHelper={false}
                        />
                    </Box>
                    {translateCommon("word-link.and")}{" "}
                    <Box className={classes.edgesScenarioRight}>
                        <MultipleAutocompleteComponent
                            inputProps={{}}
                            variant="standard"
                            freeSolo={false}
                            classes={{ popper: classes.popper }}
                            name={"scenarioTemp.triggerMax"}
                            multiple={false}
                            options={optionTriggerMax}
                            fullWidth={true}
                            filterOptions={filterOptionsFn}
                            onOpen={() => {
                                if (!fieldTriggerMax) {
                                    helpersTriggerMax.setValue(fieldTriggerMax.value, false);
                                }
                            }}
                            isDisplayHelper={false}
                        />
                    </Box>
                    {t(
                        `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.trigger-part-two`
                    )}
                </Box>

                {metaTriggerMin.error && (metaTriggerMin.touched || metaTriggerMax.touched) && (
                    <FormHelperText error>
                        {metaTriggerMin.error && (metaTriggerMin.touched || metaTriggerMax.touched)
                            ? metaTriggerMin.error
                            : " "}
                    </FormHelperText>
                )}

                {metaTriggerMax.error &&
                    (metaTriggerMin.touched || metaTriggerMax.touched) &&
                    metaTriggerMax.error !== metaTriggerMin.error && (
                        <FormHelperText error>
                            {metaTriggerMax.error &&
                            (metaTriggerMin.touched || metaTriggerMax.touched) &&
                            metaTriggerMax.error !== metaTriggerMin.error
                                ? metaTriggerMax.error
                                : " "}
                        </FormHelperText>
                    )}

                <Box className={classes.subTitleScenarioDialogContainer}>
                    <span className={classes.subTitleScenarioDialog}>
                        {t(
                            `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.audience-title`
                        )}
                    </span>
                </Box>

                <Box>
                    <RadioGroupComponent
                        options={audienceField.option.map((elt) => {
                            return {
                                ...elt,
                                name: translateCommon(`enums.audience.${elt.name}`),
                            };
                        })}
                        handleChange={audienceField.handleChange}
                        name={audienceField.name}
                    />
                </Box>

                <Box className={classes.subTitleScenarioDialogContainer}>
                    <span className={classes.subTitleScenarioDialog}>
                        {t(
                            `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.offer-characteristic-title`
                        )}
                    </span>
                </Box>

                <Grid container spacing={3}>
                    <OfferCharacteristicSharedInput
                        recurrenceValue={recurrenceValue}
                        isScenario={true}
                        setFieldValueScenario={setFieldValueScenario}
                        isSpecialOperation={isSpecialOperation}
                        t={t}
                        serviceAndChannelField={serviceAndChannelField}
                        offerCharacteristicField={offerCharacteristicField}
                        maxItems={maxItems}
                        productField={productField}
                        selectedChannel={selectedChannel}
                        selectedSource={selectedSource}
                        selectedPosList={selectedPosList}
                        categoryItemListField={categoryItemListField}
                        setFieldValue={setFieldValue}
                        discountField={discountField}
                        cartField={cartField}
                        productsFieldName={"scenarioTemp.products"}
                        fieldName={"scenarioTemp"}
                        variants={[MarketingActionVariantEnum.SmartScheduler]}
                        isCoFunded={false} // Always false for smart scheduler
                    />
                </Grid>

                <DialogActions>
                    <Button onClick={onCancel} color="primary">
                        {t(`${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.cancel`)}
                    </Button>
                    <Tooltip title={disableSubmitButtonTooltipText}>
                        <div>
                            <Button
                                onClick={onSave}
                                variant="contained"
                                color="primary"
                                disabled={disableSubmitButtonTooltipText !== ""}
                            >
                                {t(
                                    `${MarketingActionVariantEnum.SmartScheduler}.scenario-dialog.submit`
                                )}
                            </Button>
                        </div>
                    </Tooltip>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
